import React, { useMemo, useState } from "react";
import useNowTick from "../../../hooks/useNowTick";

const rejectReasons = [
  "Mesa no disponible",
  "Solicitud duplicada",
  "Cliente no localizado",
  "Mesa incorrecta",
  "Otro",
];

const elapsedLabel = (dateValue, nowTs) => {
  const date = dateValue ? new Date(dateValue).getTime() : null;
  if (!date || Number.isNaN(date)) return "-";
  const totalSeconds = Math.max(0, Math.floor((nowTs - date) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 1) return "Hace unos segundos";
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `Hace ${hours} h ${rest} min`;
};

export default function RfmTableRequestsPanel({
  open = false,
  onClose,
  requests = [],
  zones = [],
  processingSessionId = null,
  onApprove,
  onReject,
}) {
  // Solo corre el reloj mientras el panel esta abierto (cerrado no genera redibujados).
  const nowTs = useNowTick(open);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState(rejectReasons[0]);
  const [customReason, setCustomReason] = useState("");

  const zoneById = useMemo(() => {
    const map = new Map();
    zones.forEach((zone) => map.set(String(zone.id), zone));
    return map;
  }, [zones]);

  if (!open) return null;

  const confirmReject = () => {
    if (!rejecting) return;
    const finalReason = reason === "Otro" ? customReason.trim() : reason;
    onReject?.(rejecting, finalReason || reason);
    setRejecting(null);
    setReason(rejectReasons[0]);
    setCustomReason("");
  };

  return (
    <div className="slt-c-backdrop" role="dialog" aria-modal="true">
      <div className="slt-c-modal">
        <div className="slt-c-header">
          <div>
            <div className="slt-c-title">Solicitudes de mesa</div>
            <div className="slt-c-subtitle">{requests.length} pendientes</div>
          </div>
          <button className="slt-c-close" type="button" onClick={onClose} title="Cerrar">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="slt-c-body">
          {requests.length === 0 ? (
            <div className="slt-c-empty">No hay solicitudes pendientes</div>
          ) : (
            requests.map((request) => {
              const isProcessing = processingSessionId === request.session_uuid;
              const zone = zoneById.get(String(request.branchoffice_environment_id || request.zone_id || request.zone));
              return (
                <div className="slt-c-card" key={request.session_uuid}>
                  <div className="slt-c-card-top">
                    <div>
                      <div className="slt-c-card-table">Mesa {request.table_name || request.table_code || request.table_id}</div>
                      <div className="slt-c-card-meta">
                        {zone?.name || request.zone_name || "Zona no indicada"} · {elapsedLabel(request.requested_at, nowTs)}
                      </div>
                    </div>
                    <span className="slt-c-source">{request.client_name || "Cliente sin nombre"}</span>
                  </div>

                  <div className="slt-c-details">
                    <span className="slt-c-status-badge">{request.status || "PENDING"}</span>
                  </div>

                  <div className="slt-c-actions">

                    <button
                      className="slt-c-btn secondary"
                      type="button"
                      disabled={isProcessing}
                      onClick={() => {
                        setRejecting(request);
                        setReason(rejectReasons[0]);
                        setCustomReason("");
                      }}
                    >
                      Rechazar
                    </button>

                    <button
                      className="slt-c-btn primary"
                      type="button"
                      disabled={isProcessing}
                      onClick={() => onApprove?.(request)}
                    >
                      {isProcessing ? "Procesando..." : "Aceptar"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {rejecting ? (
        <div className="slt-c-reject-backdrop">
          <div className="slt-c-reject-card">
            <div className="slt-c-title">Rechazar solicitud</div>
            <label className="slt-c-form-label">Motivo</label>
            <select className="slt-c-form-input" value={reason} onChange={(e) => setReason(e.target.value)}>
              {rejectReasons.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
            {reason === "Otro" ? (
              <textarea
                className="slt-c-form-input slt-c-form-textarea"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Describe el motivo"
              />
            ) : null}
            <div className="slt-c-actions">
              <button className="slt-c-btn secondary" type="button" onClick={() => setRejecting(null)}>
                Cancelar
              </button>
              <button className="slt-c-btn primary" type="button" onClick={confirmReject}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
